import { memo, useEffect, useMemo, useReducer, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper'
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Backdrop from '@mui/material/Backdrop';
import Constant from '../../util/constants';
import { StyledImg } from '../common/Styled';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuidv4 } from 'uuid';
import { getDistance, getDuration } from '../../util/Display';
import { constructDirMapUrl } from '../../util/Display';
import Token from '../../util/Token';
import { useCreateTrip, useUpdateTrip } from '../../react-query/hooks/useTrip';

import { Icon } from '@iconify/react';
import CircularProgress from '@mui/material/CircularProgress';
import BalloonMarkGreen from '../../image/balloon_green.svg';
import { ReactComponent as GoogleMapsIcon } from '../../image/GoogleMaps.svg';
import { Context } from '../../context';

const { label } = Constant;

const types = {
    [label.VIEW_DIRECTIONS]: 'VIEW_DIRECTIONS',
}

const reducer = (state, action) => {
    switch(action.type) {
        case types[label.VIEW_DIRECTIONS]:
            return { ...state, [label.VIEW_DIRECTIONS]: action.value }
    }
}

const initialState = {
    [label.VIEW_DIRECTIONS]: ''
}

function TripPlanner({ route, onDirectionHandler }) {
    const { auth, user } = useContext(Context);
    const [tripState, dispatch] = useReducer(reducer, initialState);
    const createTrip = useCreateTrip();
    const updateTrip = useUpdateTrip();
    

    const legs = useMemo(() => {
        if (!isEmpty(route)) {
            const { legs } = route.routes[0];
            const distAndDuration = legs.reduce((prev, leg) => {
                prev.totalDistance = prev.totalDistance || 0;
                prev.totalDuration = prev.totalDuration || 0;
                prev.totalDuration += leg.duration.value;
                prev.totalDistance += leg.distance.value;
                return prev;
            }, {});
            return {
                waypoints: legs,
                end: legs[legs.length - 1],
                distAndDuration
            }
        }
    }, [route]);

    const removeStop = (leg) => () => {
        const { start_location: { lat, lng } } = leg;
        const { directionRendererInstance, google: { maps } } = window;
        const direction = directionRendererInstance.getDirections();
        if(direction) {
            const { request: { destination, origin } } = direction;
            onDirectionHandler(
              { lat: origin.location.lat(), lng: origin.location.lng() },
              { lat: destination.location.lat(), lng: destination.location.lng() },
              { location: new maps.LatLng(lat(), lng()) },
              true
            )
        }
    }
    
    const saveTrip = () => {
        const { authenticated, setAuthenticated } = auth;
        if(!authenticated.isAuth){
            setAuthenticated({ 
              ...authenticated, 
              authPopup: !0,
              redirectTo: Constant.paths.HOME,
            });
            return;
          }

        const { location } = window;
        const { legs } = route.routes[0];
        const waypoint = [...legs];
        const to = legs[legs.length-1];
        const from = legs[0];
        const url = new URL(location.href);
        let via = [];

        if (legs.length === 2) {
            via.push({
                latitude: from.end_location.lat(),
                longitude: from.end_location.lng(),
                address: from.end_address
            })
        } else if(legs.length > 2) {
            waypoint.shift();
            via = waypoint.map((way) => ({
                latitude: way.start_location.lat(),
                longitude: way.start_location.lng(),
                address: way.start_address
            }))
        }

        const token = Token.parsedToken();
        const tripReqBody = {
            user: token.data._id,
            from: {
                latitude: from.start_location.lat(),
                longitude: from.start_location.lng(),
                address: from.start_address
            },
            to: {
                latitude: to.end_location.lat(),
                longitude: to.end_location.lng(),
                address: to.end_address
            },
            via,
            ...(url.searchParams.has('tripId') && {
                _id: url.searchParams.get('tripId')
            }),
        }
        if (!url.searchParams.has('tripId'))
            createTrip.mutate(tripReqBody);

        if (url.searchParams.has('tripId')) 
            updateTrip.mutate(tripReqBody)
    }
    // construct the google map url
    useEffect(() => {
        if (route) {
            constructDirMapUrl({})
            .then((url) => {
                dispatch({
                    type: types[label.VIEW_DIRECTIONS],
                    value: url
                });
            }).catch((err) => {
                console.log(err);
            });
        }
    }, [route]);

    const handleShare = async (e) => {
        e.preventDefault();
        const urlToShare = tripState[label.VIEW_DIRECTIONS];
        if (!urlToShare) return;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'ChargeMiles Route Plan',
                    text: 'Check out this route plan I made on ChargeMiles!',
                    url: urlToShare,
                });
            } catch (err) {
                console.log('Error sharing:', err);
            }
        } else if (navigator.clipboard) {
            try {
                await navigator.clipboard.writeText(urlToShare);
                // Optionally could add a toast here, but native copy works fine for now
                alert('Route link copied to clipboard!');
            } catch (err) {
                console.log('Error copying to clipboard:', err);
            }
        }
    };

    return (
        <Box className="MuiDrawer-paper__trip-planner">
            <Typography variant="h5" sx={{ background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: '#fff', p: 2.5, m: 0 }}>{label.TRIP_PLANNER}</Typography>
            {(createTrip.isLoading || updateTrip.isLoading) && <div className='overlay'> <CircularProgress /> </div>}
            {!isEmpty(legs) && <Grid container>
                <Grid item xs={12} md={12}>
                    <Box className="px-3 py-2">
                        <Stack className="trip-panner__total py-2" justifyContent="space-between" direction="row" alignItems="center">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" color="var(--app-color)" component="span" fontWeight={500}>Total Distance:</Typography>
                                <Typography variant="subtitle1" color="text.primary" component="span" fontWeight={700} ml={1}>{getDistance(legs.distAndDuration.totalDistance)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" color="var(--app-color)" component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}><Icon icon="eva:clock-outline" width={20} height={20} style={{ marginRight: 4 }} /> Approx:</Typography>
                                <Typography variant="subtitle1" color="text.primary" component="span" fontWeight={700} ml={1} sx={{ lineHeight: 1 }}>{getDuration(legs.distAndDuration.totalDuration)}</Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Grid>
                <Grid item xs={12} md={12} className="py-2">
                    <Stack justifyContent="space-around" direction="row">
                        <Tooltip title="Share Route" placement="top">
                            <Box className="trip-planner--share-trip trip-action" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <IconButton onClick={handleShare} color="primary">
                                    <Icon icon="eva:share-outline" width={24} height={24} />
                                </IconButton>
                                <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 500, color: 'text.secondary' }}>{label.SHARE}</Typography>
                            </Box>
                        </Tooltip>
                        <Tooltip title="Save Route" placement="top">
                            <Box className="trip-planner--save-trip trip-action" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <IconButton onClick={saveTrip} color="primary">
                                    <Icon icon="eva:bookmark-outline" width={24} height={24} />
                                </IconButton>
                                <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 500, color: 'text.secondary' }}>{label.SAVE}</Typography>
                            </Box>
                        </Tooltip>
                        <Tooltip title="View in Google Maps" placement="top">
                            <Box className="trip-planner--save-trip trip-action" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <IconButton component="a" href={tripState[label.VIEW_DIRECTIONS]} target="_blank" color="primary">
                                    <GoogleMapsIcon height={24} width={24} />
                                </IconButton>
                                <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 500, color: 'text.secondary' }}>
                                    {label.VIEW_DIRECTIONS}
                                </Typography>
                            </Box>
                        </Tooltip>
                    </Stack>
                </Grid>
                {/* Starting Point */}
                <Grid item xs={12} md={12}>
                    <Box className="px-3 py-3">
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1, fontStyle: 'italic' }}>
                            Click on multiple location balloons along the route as stopover point
                        </Typography>
                        <Stack className="trip-planner__start-point py-2" direction="row" spacing={2} alignItems="center">
                            <Icon icon="eva:pin-fill" width={32} height={32} style={{ color: '#00AB55', flexShrink: 0 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2" sx={{ color: '#00AB55', fontWeight: 500 }}>
                                    {label.STARTING_POINT}
                                </Typography>
                                <Typography variant="subtitle1" color="text.primary" className="trip-planner__location" fontWeight={600} noWrap>{legs.waypoints[0].start_address}</Typography>
                            </Box>
                        </Stack>
                    </Box>
                    <Divider variant="middle" />
                </Grid>
                {/* Legs mapping (Distance -> Next Point) */}
                {legs.waypoints.map((leg, idx) => {
                    const isLast = idx === legs.waypoints.length - 1;
                    return (
                    <Grid item xs={12} md={12} key={uuidv4()}>
                        <Box className="px-3 py-3">
                            <Stack className="trip-panner__metric-duration" direction="row" alignItems="center">
                                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                    <Chip label={leg.distance.text} sx={{ bgcolor: 'rgba(0, 171, 85, 0.16)', color: 'var(--app-color)', fontWeight: 600 }} />
                                </Box>
                                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <Icon icon="eva:clock-outline" width={24} height={24} style={{ color: 'var(--app-color)' }} />
                                    <Typography variant="subtitle2" component="span" color="var(--app-color)" className='trip-planner__duration ml-1' sx={{ fontWeight: 600 }}>{leg.duration.text}</Typography>
                                </Box>
                            </Stack>
                            <Stack className="trip-planner__start-point py-2" direction="row" spacing={2} alignItems="center">
                                <Icon icon="eva:pin-fill" width={32} height={32} style={{ color: isLast ? '#FF4842' : 'var(--app-color)', flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {isLast && 
                                    <Typography variant="subtitle2" sx={{ color: '#FF4842', fontWeight: 500 }}>
                                        {label.DESTINATION_POINT}:
                                    </Typography>}
                                    <Typography variant="subtitle1" color="text.primary" className="trip-planner__location" fontWeight={600} noWrap>{leg.end_address}</Typography>
                                </Box>
                                {!isLast && 
                                <Tooltip title="Remove Stop" placement="left" PopperProps={{ disablePortal: true }}>
                                <IconButton onClick={removeStop({ start_location: leg.end_location })} size="small" aria-label="remove stop" sx={{ flexShrink: 0 }}>
                                    <Icon icon="eva:close-outline" width={24} height={24} />
                                </IconButton>
                                </Tooltip>}
                            </Stack>
                        </Box>
                        {!isLast && <Divider variant="middle" />}
                    </Grid>
                )})}
            </Grid>}
        </Box>
    )
}

export default memo(TripPlanner);